import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import YRayViewer from '../components/yray/YRayViewer';
import { ExtractionStatus, Timeline, YFrame } from '../components/yray/types';
import { isOwnerAccess, ProjectAccess } from '../hooks/useProjectAccess';
import './YRay.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const POLL_MS = 4000;

/**
 * Item page of a Y-Ray (/projects/:projectId/item/y-ray/:videoId): the result for one video,
 * read-only, so anyone who can open the project can view it. Running it again lives on /y-ray.
 */
const YRayItem: React.FC = () => {
  const { projectId, videoId } = useParams<{ projectId: string; videoId: string }>();

  const [project, setProject] = useState<{ name: string; access?: ProjectAccess } | null>(null);
  const [status, setStatus] = useState<ExtractionStatus | null>(null);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [frames, setFrames] = useState<YFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const owner = isOwnerAccess(project?.access);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/projects/${projectId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(
        r.status === 401 ? 'Your session has expired. Please sign in again.'
          : r.status === 404 ? 'Project not found for this account.'
          : `Could not load the project (HTTP ${r.status}).`
      ))))
      .then(p => { if (!cancelled) setProject({ name: p.name, access: p.access }); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [projectId]);

  const loadStatus = useCallback(async (quiet = false) => {
    if (!videoId) return;
    try {
      const res = await fetch(`${API_BASE}/api/videos/${videoId}/extraction`, { credentials: 'include' });
      if (!res.ok) throw new Error(res.status === 404 ? 'This Y-Ray was not found.' : 'Failed to load Y-Ray');
      setStatus(await res.json());
      setError('');
    } catch (e: any) {
      if (!quiet) setError(e.message || 'Failed to load Y-Ray');
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    setStatus(null);
    setTimeline(null);
    setFrames([]);
    setLoading(true);
    void loadStatus();
  }, [loadStatus]);

  const runStatus = status?.latest?.status;
  const active = !!runStatus && runStatus !== 'completed' && runStatus !== 'failed';
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => { void loadStatus(true); }, POLL_MS);
    return () => clearInterval(t);
  }, [active, loadStatus]);

  // The timeline and its thumbnails load once per finished run.
  const resultJobId = status?.result?.jobId;
  useEffect(() => {
    if (!resultJobId || !videoId) { setTimeline(null); setFrames([]); return; }
    let cancelled = false;
    fetch(`${API_BASE}/api/videos/${videoId}/metadata`, { credentials: 'include' })
      .then(async r => {
        if (r.ok) return r.json();
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load the Y-Ray result');
      })
      .then(t => { if (!cancelled) setTimeline(t); })
      .catch(e => { if (!cancelled) setError(e.message); });
    fetch(`${API_BASE}/api/videos/${videoId}/frames`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { frames: [] }))
      .then(d => { if (!cancelled) setFrames(Array.isArray(d.frames) ? d.frames : []); })
      .catch(() => { /* thumbnails are optional */ });
    return () => { cancelled = true; };
  }, [resultJobId, videoId]);

  const name = status?.video ? (status.video.name || status.video.fileName) : 'Y-Ray';
  const run = status?.latest;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="breadcrumb">
          <BreadcrumbBack to={`/projects/${projectId}`} />
          <Link to="/projects">Projects</Link>
          <span className="breadcrumb-sep">›</span>
          <Link to={`/projects/${projectId}`}>{project?.name || '…'}</Link>
          <span className="breadcrumb-sep">›</span>
          <span className="yr-crumb-last">🔎 {name}</span>
        </div>

        <div className="yr-head">
          <div className="yr-head-title">
            <h2 className="yr-item-title">{name}</h2>
            <p className="yr-sub">Y-Ray: who is on screen, what they do and what is said, moment by moment.</p>
          </div>
          {owner && (
            <div className="yr-controls">
              <Link className="btn-secondary yr-btn yr-linkbtn" to={`/projects/${projectId}/y-ray?video=${videoId}`}>
                Run again
              </Link>
            </div>
          )}
        </div>

        {error && <div className="error-banner">⚠️ {error}</div>}

        {loading ? (
          <div className="yr-card yr-center"><div className="spinner" /></div>
        ) : (
          <>
            {active && (
              <div className="yr-card yr-run">
                <div className="yr-run-title">{runStatus === 'queued' ? 'Waiting for a worker…' : 'Analyzing the video…'}</div>
                <div className="yr-progress">
                  <div className="yr-progress-fill yr-indeterminate" />
                </div>
                <p className="yr-mut">This page updates by itself when the analysis finishes.</p>
              </div>
            )}
            {runStatus === 'failed' && !status?.result && (
              <div className="yr-card yr-failed">
                <div className="yr-run-title">The Y-Ray run failed</div>
                <p className="yr-mut">{run?.error || 'Unknown error.'}</p>
                {owner && (
                  <Link className="btn-primary yr-btn yr-linkbtn" to={`/projects/${projectId}/y-ray?video=${videoId}`}>Try again</Link>
                )}
              </div>
            )}
            {timeline && status?.result && (
              <YRayViewer timeline={timeline} videoUrl={status.result.videoUrl} frames={frames} />
            )}
            {status?.result && !timeline && !error && (
              <div className="yr-card yr-center"><div className="spinner" /></div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default YRayItem;
